import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, X, Download, ExternalLink } from 'lucide-react';
import QualityPolicy from './components/QualityPolicy';
import BrandLogo from '@/components/feature/BrandLogo';
import { content, type Certificate, type Lang } from './content';

const asset = (id: string) => `${import.meta.env.BASE_URL}assets/${id}.png`;
const documentUrl = (file: string) => `${import.meta.env.BASE_URL}assets/docs/${file}`;

export default function Home({ lang = 'pt' }: { lang?: Lang }) {
  const t = content[lang];
  const [selected, setSelected] = useState(t.certificates.items[0]);
  const dialog = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    document.documentElement.lang = t.htmlLang;
    document.title = t.title;
    document.querySelector('meta[name="description"]')?.setAttribute('content', t.description);
  }, [t]);
  function openCertificate(cert: Certificate) {
    setSelected(cert);
    dialog.current?.showModal();
  }
  return (
    <div className="certification-site">
      <main>
        <header className="hero">
          <div className="hero-photo" style={{ backgroundImage: `url(${asset('laboratory-background')})` }} aria-hidden="true" />
          <nav className="language-switch" aria-label={t.languageSwitch}>
            <Link to="/" hrefLang="pt-BR" lang="pt-BR" aria-current={lang === 'pt' ? 'page' : undefined}>PT</Link>
            <Link to="/en" hrefLang="en" lang="en" aria-current={lang === 'en' ? 'page' : undefined}>EN</Link>
          </nav>
          <div className="hero-content">
            <div className="hero-brand"><BrandLogo /></div>
            <p className="eyebrow">{t.hero.eyebrow}</p>
            <h1>{t.hero.title.map((line, i) => <span key={line}>{i > 0 && <br />}{line}</span>)}</h1>
            <div className="hero-rule" />
          </div>
        </header>
        <QualityPolicy t={t.policy} />
        <section className="certificates" aria-labelledby="certificates-heading">
          <h2 id="certificates-heading" className="section-instruction">{t.certificates.instruction}</h2>
          <div className="certificate-grid">
            {t.certificates.items.map(cert => (
              <article className={`certificate-card ${cert.id}`} key={cert.id}>
                <button className="seal-button" onClick={() => openCertificate(cert)} aria-label={t.certificates.viewSeal(cert.name)}>
                  <img className="seal" src={asset(cert.id)} alt={t.certificates.sealAlt(cert.name)} width="260" height="260" />
                </button>
                <h3>{cert.name}</h3>
                <p>{cert.subtitle}{cert.detail && <><br /><span>{cert.detail}</span></>}</p>
                <button className="certificate-button" onClick={() => openCertificate(cert)}><FileText size={19} aria-hidden="true" />{t.certificates.viewButton}</button>
              </article>
            ))}
          </div>
        </section>
      </main>
      <footer className="brand-footer">

        <div className="footer-mobile"><BrandLogo inverse /><p>{t.footer.tagline}</p></div>
        <p className="developer-credit">{t.footer.credit} <a href="https://www.serraconsultoria.com" target="_blank" rel="noopener noreferrer">Serra Consultoria</a></p>
      </footer>
      <dialog ref={dialog} className="certificate-dialog" onClick={event => { if (event.target === dialog.current) dialog.current?.close(); }}>
        <div className="dialog-heading"><h2>{selected.name}</h2><button autoFocus onClick={() => dialog.current?.close()} aria-label={t.dialog.close}><X /></button></div>
        <img className="dialog-seal" src={asset(selected.id)} alt={t.certificates.sealAlt(selected.name)} />
        <p>{selected.subtitle}</p>
        {selected.documents.length > 0 ? (
          <div className="dialog-documents">
            {selected.documents.map(doc => (
              <div className="dialog-document" key={doc.file}>
                <a className="certificate-button" href={documentUrl(doc.file)} download={doc.file}><Download size={18} aria-hidden="true" />{doc.download}</a>
                <a className="document-view" href={documentUrl(doc.file)} target="_blank" rel="noopener noreferrer"><ExternalLink size={15} aria-hidden="true" />{doc.open}</a>
              </div>
            ))}
          </div>
        ) : (
          <>
            <p className="document-note">{t.dialog.unavailable}</p>
            <a className="certificate-button" href={asset(selected.id)} download={t.dialog.sealFile(selected.id)}><Download size={18} aria-hidden="true" />{t.dialog.downloadSeal}</a>
          </>
        )}
      </dialog>
    </div>
  );
}
